import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../ui/buttons/Button.jsx";
import SearchInput from "../../ui/inputs/SearchInput.jsx";
import { templatesService } from "../../services/templates.service.js";
import {
  formatFileSize,
  getAttachmentDisplayName,
  getAttachmentSizeInBytes,
} from "../../utils/fileUtils.js";
import ModalOverlay from "../../ui/Modals/ModalOverlay.jsx";

function getAttachmentDisplaySize(attachment) {
  const bytes = getAttachmentSizeInBytes(attachment);
  return bytes === null ? "" : formatFileSize(bytes);
}

export const UPLOAD_LABEL = "Subir nuevo archivo";
export const UPLOAD_AND_SAVE_LABEL = "Guardar en la cuenta y adjuntar";
export const SEND_WITHOUT_SAVING_LABEL = "Enviar sin guardar";
export const SEND_WITHOUT_SAVING_HINT =
  "Sin guardar, el archivo va solo a esta conversación y no queda entre los archivos adjuntos de la cuenta.";

function AttachmentPickerModal({
  linkedAttachmentIds = [],
  accountId,
  onClose,
  onSelectExisting,
  onUploadNew,
  onUploadWithoutSaving = null,
  onError,
  accept = ".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.csv,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.zip",
}) {
  const fileInputRef = useRef(null);
  const directFileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let isActive = true;

    const fetchAvailableAttachments = async () => {
      try {
        const response = await templatesService.fetchTemplateFilesAttachments(
          accountId ? { account_id: accountId } : {},
        );
        if (isActive) setAttachments(response.results || []);
      } catch (error) {
        console.error("Error fetching available attachments:", error);
        if (isActive) {
          setAttachments([]);
          onError?.("No se pudieron cargar los archivos adjuntos disponibles.");
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchAvailableAttachments();

    return () => {
      isActive = false;
    };
  }, [onError, accountId]);

  const filteredAttachments = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    if (!normalizedTerm) return attachments;

    return attachments.filter((attachment) =>
      getAttachmentDisplayName(attachment).toLowerCase().includes(normalizedTerm),
    );
  }, [attachments, searchTerm]);

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) onUploadNew?.(file);
    event.target.value = "";
  };

  const handleDirectFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) onUploadWithoutSaving?.(file);
    event.target.value = "";
  };

  return (
    <ModalOverlay
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
      }}
      onClose={onClose}
    >
      <div
        role="dialog"
        aria-label="Seleccionar archivo adjunto"
        style={{
          width: 420,
          maxHeight: "70vh",
          background: "var(--dropdown-surface-default)",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.18)",
          borderRadius: 16,
          padding: 24,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          fontFamily: "DM Sans",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--text-primary)" }}>
          Archivos adjuntos
        </h3>

        <SearchInput
          value={searchTerm}
          onChange={(value) => setSearchTerm(value ?? "")}
          placeholder="Buscar adjunto..."
          minChars={0}
          debounceMs={0}
          height={40}
        />

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minHeight: 120,
          }}
        >
          {isLoading ? (
            <span style={{ color: "var(--text-tertiary)", fontSize: 13 }}>
              Cargando adjuntos...
            </span>
          ) : filteredAttachments.length === 0 ? (
            <span style={{ color: "var(--text-tertiary)", fontSize: 13 }}>
              No hay archivos adjuntos disponibles.
            </span>
          ) : (
            filteredAttachments.map((attachment) => {
              const isLinked = linkedAttachmentIds.includes(attachment.id);
              const displaySize = getAttachmentDisplaySize(attachment);

              return (
                <button
                  key={attachment.id}
                  type="button"
                  disabled={isLinked}
                  onClick={() => onSelectExisting?.(attachment)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-primary, rgba(0,0,0,0.1))",
                    background: "transparent",
                    cursor: isLinked ? "default" : "pointer",
                    opacity: isLinked ? 0.5 : 1,
                    textAlign: "left",
                    fontFamily: "DM Sans",
                    fontSize: 13,
                    color: "var(--text-primary)",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getAttachmentDisplayName(attachment)}
                    </span>

                    {displaySize && (
                      <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                        {displaySize}
                      </span>
                    )}
                  </span>

                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      color: isLinked
                        ? "var(--text-tertiary)"
                        : "var(--color-brand-600, var(--text-secondary))",
                    }}
                  >
                    {isLinked ? "Ya vinculado" : "Vincular"}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {onUploadWithoutSaving && (
          <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{SEND_WITHOUT_SAVING_HINT}</span>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <Button variant="secondary" tone="blue" height={40} onClick={onClose}>
            Cancelar
          </Button>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {onUploadWithoutSaving && (
              <Button
                variant="secondary"
                tone="blue"
                height={40}
                onClick={() => directFileInputRef.current?.click()}
              >
                {SEND_WITHOUT_SAVING_LABEL}
              </Button>
            )}

            <Button
              tone="blue"
              height={40}
              onClick={() => fileInputRef.current?.click()}
            >
              {onUploadWithoutSaving ? UPLOAD_AND_SAVE_LABEL : UPLOAD_LABEL}
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          style={{ display: "none" }}
          data-testid="attachment-picker-file-input"
          onChange={handleFileInputChange}
        />

        {onUploadWithoutSaving && (
          <input
            ref={directFileInputRef}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            data-testid="attachment-picker-direct-file-input"
            onChange={handleDirectFileInputChange}
          />
        )}
      </div>
    </ModalOverlay>
  );
}

export default AttachmentPickerModal;
